import React, { FC, Fragment, useEffect, useState } from 'react';

import {
  Grid,
  makeStyles,
  TextField,
  Typography,
  Box
} from '@material-ui/core';
import {
  DatePicker,
  MuiPickersUtilsProvider
  // TimePicker
} from '@material-ui/pickers';

import { useObserver } from 'mobx-react-lite';

import MomentUtils from '@date-io/moment';
import moment from 'moment-timezone';

import { isEmpty, pick, isUndefined } from 'lodash';
import { isNullOrUndefined } from 'util';

import {
  defaultTimezone,
  timestampFormat
} from '../../../../domain/models/Constants';
import PhoneTextField from '../../../Core/PhoneTextField/PhoneTextField';
import {
  useValidators,
  Validators,
  ValidationErrors,
  FieldValidators
} from '../../../Core/Validators';
import SelectOrderType from './SelectOrderType';
import SelectOrderTimeType from './SelectOrderTimeType';
import { LayoutVariant } from '../../../Core/CommonTypes';
import { OnlineOrder, OnlineOrderingStore, Place } from 'kiolyn-api';
import {
  OnlineOrderType,
  OnlineOrderSubmitConfiguration
} from '../../../../domain/models/CommonTypes';
import GooglePlaceAutocomplete from '../../../Core/GooglePlaceAutcocomplete/GooglePlaceAutocomplete';
import {
  getComponentAddressByPlace,
  PlaceType
} from '../../../../core/GoogleMaps';
import GoogleDistanceCalculator from '../../../Core/GoogleDistanceCalculator/GoogleDistanceCalculator';
import CurrencyFormat from '../../../Core/CurrencyFormat/CurrencyFormat';
import { useLocaleConfig } from '../../../../stores/LocaleConfigProvider';
import BizHour from '../../BizHour/BizHour';
import { useOrdering } from '../../../../stores/OrderingStore';
import EstimatedDeliveryInfo from './EstimatedDeliveryInfo';
import TimeSlotOrder from './TimeSlotOrder';

const useStyles = makeStyles(theme => ({
  firstRow: {
    marginBottom: theme.spacing(2)
  },
  dateTime: {
    marginTop: theme.spacing(1),
    minWidth: theme.spacing(30),
    maxWidth: 320
  },
  mobileDateTime: {
    width: '100%'
  },
  deliveryNotes: {
    marginTop: theme.spacing(4)
  },
  deliveryDetail: {
    marginTop: theme.spacing(2)
  }
}));

interface OrderInfoProps {
  config: OnlineOrderSubmitConfiguration;
  onUpdateCalculatedValues: (order: OnlineOrder) => void;
  onErrorsChange: (e: ValidationErrors) => void;
  disabled?: boolean;
  isLandingPage?: boolean;
  variant?: LayoutVariant;
  isOrderFromTable?: boolean;
}

const OrderInfo: FC<OrderInfoProps> = ({
  config,
  onUpdateCalculatedValues,
  onErrorsChange,
  disabled,
  isLandingPage,
  variant,
  isOrderFromTable
}) => {
  const orderingStore = useOrdering();
  const order = orderingStore.order;
  const localeConfig = useLocaleConfig();
  const classes = useStyles();
  const fullDetail = isLandingPage !== true;
  const { bizHours, holidays, deliverySettings, acceptedOrderTypes } = config;
  const acceptDeliveryAmount = deliverySettings?.accept_delivery_amount ?? 0;
  const freeDeliveryAmount = deliverySettings?.free_delivery_amount ?? 0;
  const maxDeliveryDistance = deliverySettings?.maximum_delivery_distance ?? 0;
  const defaultDeliveryTime = deliverySettings?.delivery_time ?? 20;
  const deliveryType = deliverySettings?.delivery_type ?? '';
  const store = orderingStore.store as OnlineOrderingStore;

  const funcEnableDatePicker = () =>
    !order.is_asap &&
    order.order_type !== 'dinein' &&
    store.online_order_catering_settings?.enable_online_catering &&
    (((deliveryType === 'postmates' || deliveryType === 'doordash') &&
      order.order_type !== 'delivery') ||
      (deliveryType !== 'postmates' && deliveryType !== 'doordash'));
  const enableDatePicker = funcEnableDatePicker();

  const getDeliveryTime = () => orderingStore.getOrderDeliveryTime();
  const getDelayTime = () => orderingStore.getOrderDelayTime();
  const validateBizHours = (pickupTime?: string) =>
    orderingStore.validateBizHoursPickupTime(pickupTime);

  const getPreparationTimeErrorMessage = () => {
    let errorMessage = `We need at least ${getDelayTime()} mins to prepare`;
    if (order.order_type === 'delivery') {
      errorMessage += ` and ${getDeliveryTime()} mins to delivery`;
    }
    return `${errorMessage} your order`;
  };

  const momentTimeZone = () => {
    return moment().tz(orderingStore.store.timezone || defaultTimezone);
  };
  const getTotalDelayTime = () => {
    if (order.order_type === 'delivery') {
      return getDelayTime() + getDeliveryTime();
    }

    return getDelayTime();
  };

  const acceptOrderFromTime = () =>
    momentTimeZone().add(getDelayTime() + getDeliveryTime(), 'minutes');

  const acceptOrderToTime = () => momentTimeZone().add(30, 'days');
  let isWorking = true;
  const pickupTime =
    order.time ??
    momentTimeZone()
      .add(getDelayTime(), 'minutes')
      .format(timestampFormat);
  const result = validateBizHours(pickupTime);
  if (result !== null) {
    isWorking = false;
    order.is_asap = false;
  }

  const fieldValidators: FieldValidators = {
    customer_place: [
      place => {
        if (
          order.order_type === 'pickup' ||
          order.order_type === 'dinein' ||
          (!isNullOrUndefined(place) && !isNullOrUndefined(place.place_id))
        ) {
          return null;
        }
        return Validators.required.name;
      }
    ],
    time: [
      value => {
        if (order.is_asap) {
          return null;
        }
        if (isEmpty(value)) {
          return 'required';
        }
        const t = moment(value, timestampFormat).tz('US/Pacific', true);
        if (t.isBefore(acceptOrderFromTime())) {
          return 'preparationTime';
        }
        if (t.isAfter(acceptOrderToTime())) {
          return 'preparationTime30';
        }
        return null;
      },
      value => validateBizHours(value)
    ],
    delivery_distance: [
      distance => {
        if (
          order.order_type === 'pickup' || // Pickup accept any amount
          order.order_type === 'dinein' || // Dinein accept any amount
          maxDeliveryDistance === 0 // Not settings for minimum amount
        ) {
          return true;
        }
        if (distance < 0) {
          return 'noroute';
        } else if (distance > maxDeliveryDistance) {
          return 'exceed';
        }
        return true;
      }
    ],
    quote_id: [
      quoteId => {
        if (
          order.order_type === 'delivery' &&
          deliveryType === 'postmates' &&
          isEmpty(quoteId)
        ) {
          return 'noQuote';
        }
        return true;
      }
    ]
  };

  if (fullDetail) {
    // Extra validations when in full detail mode
    Object.assign(fieldValidators, {
      customer_first_name: [Validators.required],
      customer_last_name: [Validators.required],
      customer_email: [Validators.required, Validators.emailFormat],
      customer_phone: [Validators.required],
      total: [
        (_: number) => {
          const subtotal = order?.subtotal ?? 0;
          if (
            deliveryType === 'postmates' || // Postmates accept any amount
            order.order_type === 'pickup' || // Pickup accept any amount
            order.order_type === 'dinein' || // Dinein accept any amount
            subtotal >= acceptDeliveryAmount // Must greater than minimum amount
          ) {
            return null;
          }
          return 'tooSmallForDelivery';
        }
      ]
    });
  }

  const errorMessages: { [name: string]: { [name: string]: string } } = {
    customer_place: {
      required: 'Address is required'
    },
    customer_email: {
      required: 'Email is required',
      emailFormat: 'Email is invalid'
    },
    customer_phone: {
      required: 'Phone is required'
    },
    customer_first_name: {
      required: 'First Name is required'
    },
    customer_last_name: {
      required: 'Last Name is required'
    },
    time: {
      required: 'Time is required',
      futureDate: 'Please choose a future time',
      preparationTime: getPreparationTimeErrorMessage(),
      preparationTime30: 'Please choose date within next 30 days',
      bizHours: 'Store is closed at selected time'
    },
    total: {
      tooSmallForDelivery: `Order total amount is too small for delivery.`
    },
    delivery_distance: {
      exceed: `Address is out of delivery distance`,
      noroute: `Delivery cannot be made to this address`
    },
    quote_id: {
      noQuote: 'No suitable delivery for this address'
    }
  };

  const [orderTime, setOrderTime] = useState(
    order.timeIsoFormat ? new Date(order.timeIsoFormat) : undefined
  );
  useEffect(() => {
    if (order.time) {
      setOrderTime(
        moment(order.time, timestampFormat)
          .tz(orderingStore.store.timezone || defaultTimezone, true)
          .toDate()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors, validate, validateObject] = useValidators(
    order,
    fieldValidators,
    onErrorsChange
  );

  const validateOrder = () =>
    validateObject(order, [
      'customer_place',
      'total',
      'delivery_distance',
      'time'
    ]);

  const handleOrderTypeChange = (type: OnlineOrderType) => {
    if (isEmpty(type)) {
      return;
    }

    orderingStore.setOrderType(type);

    if (funcEnableDatePicker() === false) {
      handleOrderTimeChange(undefined);
    }
    validateOrder();
  };

  const handleOrderTimeTypeChange = (asap: boolean) => {
    handleValueChange('is_asap', asap);
    if (asap === true) handleOrderTimeChange(undefined);
  };

  const handleOrderTimeChange = (time?: moment.Moment) => {
    order.time = time?.format(timestampFormat);
    order.timeIsoFormat = time
      ?.clone?.()
      ?.tz?.(moment.tz.guess())
      ?.format?.();
    setOrderTime(time?.toDate());
    validate('time', order.time);
    orderingStore.checkOrderByTimeChange();
  };

  const handleDeliveryDistanceChange = (distance: number) => {
    order.delivery_distance = distance;
    onUpdateCalculatedValues(order);
    validateOrder();
  };

  const handleCustomerPlaceChange = (place: Place) => {
    getComponentAddressByPlace(place).then(dataPlace => {
      if (dataPlace) {
        handleBillingPlaceChange(dataPlace);
        handleValueChange('customer_place', dataPlace);
      }
    });
  };

  const handleBillingPlaceChange = (place?: Place) => {
    order.billing_place = place;
    order.billing_address = order.billing_place?.description;
    order.billing_zip = (place as any).zip_code;

    validate('billing_place', order.billing_place);
  };

  const handleValueChange = (name: string, value: any) => {
    const o = order as { [name: string]: any };
    o[name] = value;
    validate(name, value);
  };

  const selectOrderType = useObserver(() => {
    if (acceptedOrderTypes && acceptedOrderTypes.length > 1) {
      return (
        <SelectOrderType
          disabled={disabled}
          value={order.order_type as OnlineOrderType}
          acceptedOrderTypes={acceptedOrderTypes}
          onChange={handleOrderTypeChange}
        />
      );
    }
    return null;
  });

  const selectOrderTimeType = useObserver(() => (
    <SelectOrderTimeType
      disabled={disabled}
      value={order.is_asap}
      onChange={handleOrderTimeTypeChange}
      isWorking={isWorking}
      pauseOrder={store.pause_online_ordering}
    />
  ));

  const selectTimeLabel = `${
    order.order_type === 'delivery'
      ? 'Delivery'
      : order.order_type === 'pickup'
      ? 'Pickup'
      : 'Dine-In'
  } time`;
  const selectDateLabel = `${
    order.order_type === 'delivery'
      ? 'Delivery'
      : order.order_type === 'pickup'
      ? 'Pickup'
      : 'Dine-In'
  } date`;
  const selectTime = useObserver(() => (
    <Grid container direction="column" className={classes.dateTime}>
      <TimeSlotOrder
        timezone={store.timezone}
        label={selectTimeLabel}
        disabled={disabled || order.is_asap}
        value={orderTime}
        orderType={order.order_type}
        error={!isUndefined(errors.time)}
        bizHours={bizHours}
        delayTime={getTotalDelayTime()}
        helperText={
          enableDatePicker !== true
            ? errorMessages.time[errors.time]
            : undefined
        }
        onChange={handleOrderTimeChange}
        pauseOrder={store.pause_online_ordering}
      />
    </Grid>
  ));

  const selectDate = useObserver(() => (
    <Grid container direction="column" className={classes.dateTime}>
      <MuiPickersUtilsProvider key="dateTime" utils={MomentUtils}>
        <DatePicker
          label={selectDateLabel}
          value={orderTime ?? new Date()}
          views={['date']}
          onChange={d => handleOrderTimeChange(d as moment.Moment)}
          disabled={disabled || order.is_asap}
          error={!isUndefined(errors.time)}
          helperText={
            enableDatePicker === true && errorMessages.time[errors.time]
          }
        />
      </MuiPickersUtilsProvider>
    </Grid>
  ));

  const firstnameField = useObserver(() => (
    <TextField
      fullWidth
      margin="dense"
      id="name"
      label="First Name"
      name="customer_first_name"
      key="customer_first_name"
      value={order.customer_first_name}
      onChange={e => handleValueChange(e.target.name, e.target.value)}
      error={!isUndefined(errors.customer_first_name)}
      helperText={errorMessages.customer_first_name[errors.customer_first_name]}
      required
      disabled={disabled}
      inputProps={{
        maxLength: 50
      }}
    />
  ));

  const lastnameField = useObserver(() => (
    <TextField
      fullWidth
      margin="dense"
      id="name"
      label="Last Name"
      name="customer_last_name"
      key="customer_last_name"
      value={order.customer_last_name}
      onChange={e => handleValueChange(e.target.name, e.target.value)}
      error={!isUndefined(errors.customer_last_name)}
      helperText={errorMessages.customer_last_name[errors.customer_last_name]}
      required
      disabled={disabled}
      inputProps={{
        maxLength: 50
      }}
    />
  ));

  const emailField = useObserver(() => (
    <TextField
      fullWidth
      margin="dense"
      id="email"
      label="Email"
      type="email"
      name="customer_email"
      key="customer_email"
      value={order.customer_email}
      onChange={e => handleValueChange(e.target.name, e.target.value)}
      required
      error={!isUndefined(errors.customer_email)}
      helperText={errorMessages.customer_email[errors.customer_email]}
      disabled={disabled}
      inputProps={{
        maxLength: 100
      }}
    />
  ));

  const phoneField = useObserver(() => (
    <PhoneTextField
      fullWidth
      margin="dense"
      id="customer_phone"
      name="customer_phone"
      key="customer_phone"
      value={order.customer_phone}
      onChange={e =>
        handleValueChange(
          e.target.name,
          e.target.value.trim().replace(/[^0-9]/g, '')
        )
      }
      required
      error={!isUndefined(errors.customer_phone)}
      errorMessage={errorMessages.customer_phone[errors.customer_phone]}
      disabled={disabled}
    />
  ));

  const notes = [
    `We need at least ${getDelayTime()} mins to prepare your order`
  ];
  if (order.order_type === 'delivery') {
    notes.push(
      `Delivered from ${deliverySettings?.delivery_from_address?.description}`
    );
  }
  if (deliveryType === 'owner' && order.order_type === 'delivery') {
    notes.push(
      `We need approximately ${defaultDeliveryTime} mins to deliver your order`
    );

    if (maxDeliveryDistance > 0) {
      notes.push(`Only deliver within ${maxDeliveryDistance} miles`);
    }
    if (acceptDeliveryAmount > 0) {
      notes.push(
        `Only deliver for order greater than ${localeConfig.formatCurrency(
          acceptDeliveryAmount
        )}`
      );
    }

    if (freeDeliveryAmount > 0) {
      notes.push(
        `Free delivery for order greater than ${localeConfig.formatCurrency(
          freeDeliveryAmount
        )}`
      );
    }
  }

  const addressInputError =
    !isUndefined(errors.customer_place) ||
    !isUndefined(errors.delivery_distance);
  const addressHelperText = [
    errorMessages.customer_place[errors.customer_place],
    errorMessages.delivery_distance[errors.delivery_distance]
  ].filter(m => !isEmpty(m))[0];
  const addressInput = useObserver(() => (
    <Box>
      <Grid
        container
        direction="row"
        justify="space-between"
        spacing={2}
        wrap="nowrap"
      >
        <Grid item xs={8}>
          <GooglePlaceAutocomplete
            label="Delivery address"
            value={
              pick(order?.customer_place, [
                'place_id',
                'description'
              ]) as PlaceType
            }
            onChange={pt =>
              handleCustomerPlaceChange(
                pick(pt, ['place_id', 'description']) as Place
              )
            }
            error={addressInputError}
            helperText={addressHelperText}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            margin="dense"
            id="customer_place_unit"
            label="Unit"
            name="customer_place_unit"
            fullWidth
            value={order.customer_place_unit}
            onChange={e =>
              handleValueChange('customer_place_unit', e.target.value)
            }
          />
        </Grid>
      </Grid>

      <Box className={classes.deliveryDetail}>
        {deliveryType === 'owner' && (
          <>
            <GoogleDistanceCalculator
              origin={
                pick(deliverySettings?.delivery_from_address, [
                  'place_id',
                  'description'
                ]) as PlaceType
              }
              destination={
                pick(order?.customer_place, [
                  'place_id',
                  'description'
                ]) as PlaceType
              }
              onChange={handleDeliveryDistanceChange}
            />
            <Typography variant="subtitle1" display="block">
              <CurrencyFormat
                amount={order.delivery_fee_amount}
                prefix={'Delivery fee: '}
              />
            </Typography>
          </>
        )}

        {['postmates', 'doordash'].includes(deliveryType) && (
          <EstimatedDeliveryInfo
            asap={order.is_asap}
            destination={
              deliveryType === 'doordash'
                ? order.customer_place?.place_id
                  ? `${order.customer_place?.street},${order.customer_place?.state},${order.customer_place?.city},${order.customer_place?.zip_code}`
                  : undefined
                : order.customer_place?.description
            }
            deliveryIsoTime={order.timeIsoFormat}
            orderValue={order.total}
            onQuoteChange={q => {
              handleValueChange('quote_id', q?.id);
              validateOrder();
            }}
          />
        )}
      </Box>
    </Box>
  ));

  return useObserver(() => (
    <form noValidate autoComplete="off">
      {errors.total && (
        <Typography display="block" color="secondary">
          {errorMessages.total[errors.total]}
        </Typography>
      )}
      {isOrderFromTable ? null : variant === LayoutVariant.mobile ? (
        <Grid container direction="column" spacing={2}>
          <Grid item>{selectOrderType}</Grid>
          <Grid item>{selectOrderTimeType}</Grid>
          <Grid item>{!order.is_asap && selectTime}</Grid>
          {enableDatePicker && <Grid item>{selectDate}</Grid>}
        </Grid>
      ) : (
        <Grid
          container
          justify="space-around"
          alignItems="flex-start"
          direction="row"
          className={classes.firstRow}
          spacing={2}
        >
          <Grid item xs={4}>
            {selectOrderType}
          </Grid>
          <Grid item xs={4}>
            {selectOrderTimeType}
          </Grid>
          <Grid item xs={4}>
            {!order.is_asap && selectTime}
            {enableDatePicker && selectDate}
          </Grid>
        </Grid>
      )}
      {fullDetail && (
        <Fragment>
          <Typography variant="h6" color="textSecondary">
            Please input your detail below
          </Typography>
          {variant === LayoutVariant.mobile ? (
            [firstnameField, lastnameField]
          ) : (
            <Grid container justify="space-between" spacing={2} wrap="nowrap">
              <Grid item xs={6}>
                {firstnameField}
              </Grid>
              <Grid item xs={6}>
                {lastnameField}
              </Grid>
            </Grid>
          )}
          {variant === LayoutVariant.mobile ? (
            [emailField, phoneField]
          ) : (
            <Grid container justify="space-between" spacing={2} wrap="nowrap">
              <Grid item xs={6}>
                {emailField}
              </Grid>
              <Grid item xs={6}>
                {phoneField}
              </Grid>
            </Grid>
          )}
        </Fragment>
      )}
      {order.order_type === 'delivery' && addressInput}
      {isOrderFromTable ? null : (
        <>
          <Typography
            display="block"
            className={classes.deliveryNotes}
            color="primary"
          >
            IMPORTANT NOTES:
          </Typography>
          {orderingStore.store.biz_hours && (
            <BizHour
              bizHours={bizHours}
              holidays={holidays}
              timezone={store.timezone}
            />
          )}
          {notes.map((n, index) => (
            <Typography key={index} display="block">
              * {n}
            </Typography>
          ))}
        </>
      )}
    </form>
  ));
};

export default OrderInfo;
